import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DropResult } from '@hello-pangea/dnd';
import {
  GraduationCap,
  MagnifyingGlass,
  Plus,
  SquaresFour,
  ChartBar,
  Archive,
  FileText,
  Funnel,
  ListChecks,
} from 'phosphor-react';
import { toast } from 'sonner';
import { PageBox } from '../../../components/pageBox';
import { calculateRankBetween } from '../../../utils/lexoRank';
import { EstatutoDestino, IEstatuto, useEstatuto } from '../../../hooks/useEstatuto';

import {
  IKanbanCampoValorInput,
  IKanbanCard,
  IKanbanColumn,
  IKanbanUserOption,
  IKanbanVinculo,
  KanbanPrioridade,
  useKanban,
} from '../../../hooks/useKanban';
import { KanbanCardModal } from '../../../components/kanban/KanbanCardModal';
import { KanbanFieldFilters } from '../../../components/kanban/KanbanFieldFilters';
import { EstatutoBoard } from './components/EstatutoBoard';
import { EstatutoCreateModal } from './components/EstatutoCreateModal';
import { EstatutoQuickTaskModal } from './components/EstatutoQuickTaskModal';
import { EstatutoDashboard, EstatutoKpiRow } from './components/EstatutoDashboard';
import { EstatutoArquivados } from './components/EstatutoArquivados';
import { useEstatutoCardActions } from './hooks/useEstatutoCardActions';
import { EstatutoDadosTab } from './components/EstatutoDadosTab';
import { EstatutoAcoesSlot } from './components/EstatutoAcoesSlot';
import { EstatutoAssinaturaModal } from './components/EstatutoAssinaturaModal';
import { EstatutoPdfModal } from './components/EstatutoPdfModal';
import { EstatutoPerguntasModal } from './components/EstatutoPerguntasModal';

type EstatutoView = 'dashboard' | 'constituicao' | 'baixa' | 'alteracao' | 'arquivados';

const PROJETO_TITULO = 'Estatutos';

// As views que renderizam um board (task #492). Cada uma resolve seu projeto pelo
// `destino`; a Alteração (26/10) é só o fluxo do kanban, sem formulário.
const VIEWS_QUADRO: EstatutoView[] = ['constituicao', 'baixa', 'alteracao'];

// Botões do cabeçalho compactos (o `md` do tema tem 44px de altura e px 10); os secundários
// discretos, para o "Novo estatuto" ser o destaque.
const BOTAO_CABECALHO: ButtonProps = { size: 'sm', h: '36px', px: 3, fontSize: 'sm' };
const BOTAO_SECUNDARIO: ButtonProps = {
  ...BOTAO_CABECALHO,
  variant: 'outline',
  borderWidth: '1px',
  borderColor: 'gray.300',
  color: 'gray.700',
  bg: 'white',
  _hover: { bg: 'gray.50', color: 'gray.900' },
  _active: { bg: 'gray.100', color: 'gray.900' },
};

export const EstatutoPage = () => {
  const { listAllEstatutos, syncPendentesAutentique, getDashboard } = useEstatuto();
  const { getEstatutoProjeto, getBoard, moveCard, updateCard, arquivarCard } = useKanban();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [view, setView] = useState<EstatutoView>('dashboard');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [optimisticColumns, setOptimisticColumns] = useState<IKanbanColumn[] | null>(null);

  // Filtros do board (UX — aplicados no cache, sem backend). `filtroCampos` alimenta o
  // KanbanFieldFilters reusado (responsável + faixa de datas de expiração = SLA/data-fim);
  // prioridade e data de INÍCIO (dt_criacao) são controles próprios do módulo.
  const [showFilters, setShowFilters] = useState(false);
  const [filtroCampos, setFiltroCampos] = useState<Record<string, unknown>>({});
  const [filtroPrioridade, setFiltroPrioridade] = useState<'' | KanbanPrioridade>('');
  const [criadoDe, setCriadoDe] = useState('');
  const [criadoAte, setCriadoAte] = useState('');

  const { isOpen: isCreateOpen, onOpen: openCreate, onClose: closeCreate } = useDisclosure();
  const perguntas = useDisclosure();
  const {
    isOpen: isQuickTaskOpen,
    onOpen: openQuickTask,
    onClose: closeQuickTask,
  } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

  // Quadro ativo (task #492): a aba Baixa resolve o 2º projeto; as demais views que
  // mostram board (constituicao) e o resto usam o quadro da Constituição. Assim toda a
  // lógica de board/drag/finalização abaixo opera no projeto CORRETO sem duplicar.
  const destinoAtivo: EstatutoDestino =
    view === 'baixa' || view === 'alteracao' ? view : 'constituicao';

  // 1. Projeto do kanban do quadro ativo, resolvido pelo endpoint dedicado (task #757),
  // agora por `destino` (task #492) — não mais por `.find(titulo === 'Estatutos')`.
  const {
    data: projetoData,
    isLoading: loadingProjetos,
    error: errorProjetos,
  } = useQuery({
    queryKey: ['estatuto-kanban-projeto', destinoAtivo],
    queryFn: () => getEstatutoProjeto(destinoAtivo),
    staleTime: 5 * 60_000,
  });

  const projetoId = projetoData?.data?.projeto_id;

  // 2. Board do projeto (colunas + cards)
  const {
    data: boardData,
    isLoading: loadingBoard,
    error: errorBoard,
  } = useQuery({
    queryKey: ['kanban-board', projetoId],
    queryFn: () => getBoard(projetoId),
    enabled: !!projetoId,
    staleTime: 30_000,
  });

  // 3. Estatutos (pra juntar card ↔ estatuto por kanban_card_id).
  // Pagina de 100 em 100 — o board não é paginado e size > 100 dá 400.
  const {
    data: estatutosData,
    isLoading: loadingEstatutos,
    error: errorEstatutos,
  } = useQuery({
    queryKey: ['estatutos', 'all'],
    queryFn: listAllEstatutos,
    staleTime: 30_000,
  });

  // 4. Dashboard / totalizadores (KPIs no topo + aba Dashboard).
  // Chaveado por destino: cada quadro tem seus próprios números (antes era uma
  // query só, sem destino, e a Baixa herdava a contagem da Constituição).
  const { data: dashboardData, isLoading: loadingDashboard } = useQuery({
    queryKey: ['estatuto-dashboard', destinoAtivo],
    queryFn: () => getDashboard(destinoAtivo),
    staleTime: 30_000,
  });
  const dashboard = dashboardData?.data;

  // O endpoint do dashboard roda `ensure_colunas` no backend (cria as colunas
  // faltantes do fluxo). O endpoint do board NÃO faz isso — então, assim que o
  // dashboard responde, rebuscamos o board uma vez pra ele refletir as colunas
  // recém-garantidas (evita o board ficar com um conjunto antigo cacheado).
  const boardEnsuredRef = useRef(false);
  useEffect(() => {
    if (dashboardData && projetoId && !boardEnsuredRef.current) {
      boardEnsuredRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['kanban-board', projetoId] });
    }
  }, [dashboardData, projetoId, queryClient]);

  const estatutoByCardId = useMemo(() => {
    const map = new Map<number, IEstatuto>();
    (estatutosData ?? []).forEach(e => {
      if (e.kanban_card_id != null) map.set(e.kanban_card_id, e);
    });
    return map;
  }, [estatutosData]);

  // Ao trocar de quadro (Constituição↔Baixa), descarta o estado otimista do board
  // anterior — senão colunas de um quadro vazariam no outro se a troca ocorrer antes
  // do move liquidar (task #492).
  useEffect(() => {
    setOptimisticColumns(null);
  }, [destinoAtivo]);

  const columns: IKanbanColumn[] = optimisticColumns ?? boardData?.data?.columns ?? [];

  const moveCardMutation = useMutation({ mutationFn: moveCard });

  // ---- Finalização manual: mover o card pra coluna "Assinado / Finalizado" ----
  // (enquanto o gatilho automático por anexo do Cartão do CNPJ não existe).
  const colunaFinalizado = useMemo(
    () => columns.find(c => /finaliz/i.test(c.titulo)) ?? null,
    [columns],
  );

  const selectedEstatuto = useMemo(
    () => (estatutosData ?? []).find(e => e.id === selectedId) ?? null,
    [estatutosData, selectedId],
  );

  // O card do kanban que o modal genérico renderiza no core (título/descrição/comentários/
  // anexos/responsáveis). Guardado DIRETO do clique no board (não re-resolvido das columns):
  // quando o modal abre, o card veio do card clicado -> nunca null (evita modal em branco se
  // o board recarregar/o estatuto não estiver no destino ativo).
  const [selectedCard, setSelectedCard] = useState<IKanbanCard | null>(null);

  const selectedCardColuna = useMemo(
    () => columns.find(c => c.cards.some(card => card.id === selectedCard?.id))?.titulo ?? null,
    [columns, selectedCard],
  );

  // Só pode finalizar quando já está assinado e o card ainda não está na coluna final.
  const cardColunaAtual = useMemo(() => {
    const cardId = selectedEstatuto?.kanban_card_id;
    if (cardId == null) return null;
    return columns.find(c => c.cards.some(card => card.id === cardId)) ?? null;
  }, [selectedEstatuto, columns]);

  const canFinalizar =
    !!colunaFinalizado &&
    !!cardColunaAtual &&
    cardColunaAtual.id !== colunaFinalizado.id &&
    selectedEstatuto?.status === 'assinado_finalizado';

  const finalizarMutation = useMutation({ mutationFn: moveCard });

  const handleFinalizar = () => {
    const cardId = selectedEstatuto?.kanban_card_id;
    if (!canFinalizar || !colunaFinalizado || cardId == null) return;
    const lastCard = colunaFinalizado.cards[colunaFinalizado.cards.length - 1];
    const prevRank = lastCard?.rank ?? null;
    finalizarMutation.mutate(
      { id: cardId, column_id: colunaFinalizado.id, prev_rank: prevRank, next_rank: null },
      {
        onSuccess: () => {
          toast.success('Processo finalizado');
          queryClient.invalidateQueries({ queryKey: ['kanban-board', projetoId] });
          queryClient.invalidateQueries({ queryKey: ['estatutos', 'all'] });
          queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
        },
        onError: () => toast.error('Não foi possível finalizar. Tente novamente.'),
      },
    );
  };

  // Validação inicial ao abrir o board: sincroniza documentos Autentique
  // pendentes (recupera webhook perdido enquanto o backend esteve fora/sem WS).
  // Best-effort e não-bloqueante — o board já renderiza com o estado do banco;
  // se algo mudou, invalida pra refletir (cobre o caso de WS desligado).
  useEffect(() => {
    let active = true;
    syncPendentesAutentique()
      .then(res => {
        if (!active) return;
        if ((res?.data?.atualizados?.length ?? 0) > 0) {
          queryClient.invalidateQueries({ queryKey: ['estatutos'] });
          queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
        }
      })
      .catch(() => {
        // Silencioso: é só uma recuperação oportunista, não pode atrapalhar.
      });
    return () => {
      active = false;
    };
    // Só no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Usuários (responsáveis) para o filtro — derivados dos cards do board (sem request extra;
  // o filtro opera sobre o cache que já veio). Únicos, ordenados por nome.
  const projectUsers = useMemo<IKanbanUserOption[]>(() => {
    const userMap = new Map<number, IKanbanUserOption>();
    columns.forEach(col =>
      (col.cards || []).forEach(card =>
        (card.usuarios || []).forEach(u => {
          if (!userMap.has(u.id)) {
            userMap.set(u.id, { id: u.id, nome: u.nome || '', email: '' });
          }
        }),
      ),
    );
    return Array.from(userMap.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [columns]);

  const filteredColumns = useMemo(() => {
    const term = search.trim().toLowerCase();
    const responsavelId = filtroCampos.responsavel ? Number(filtroCampos.responsavel) : null;
    const expiraDe = filtroCampos.expiraDe ? String(filtroCampos.expiraDe) : null;
    const expiraAte = filtroCampos.expiraAte ? String(filtroCampos.expiraAte) : null;
    const criadoDeStr = criadoDe || null;
    const criadoAteStr = criadoAte || null;
    const prio = filtroPrioridade || null;

    const semFiltro =
      !term && !responsavelId && !expiraDe && !expiraAte && !criadoDeStr && !criadoAteStr && !prio;
    if (semFiltro) return columns;

    return columns.map(col => ({
      ...col,
      cards: col.cards.filter(card => {
        // Título (busca livre)
        if (term) {
          const est = estatutoByCardId.get(card.id);
          const titulo = (est?.titulo || card.titulo || '').toLowerCase();
          if (!titulo.includes(term)) return false;
        }
        // Responsável
        if (responsavelId && !(card.usuarios || []).some(u => u.id === responsavelId)) {
          return false;
        }
        // Prioridade
        if (prio && card.prioridade !== prio) return false;
        // Faixa de expiração / SLA / data-fim (compara a parte de data de `expira_em`)
        if (expiraDe || expiraAte) {
          const exp = card.expira_em ? card.expira_em.slice(0, 10) : null;
          if (!exp) return false;
          if (expiraDe && exp < expiraDe) return false;
          if (expiraAte && exp > expiraAte) return false;
        }
        // Faixa de data de início (dt_criacao)
        if (criadoDeStr || criadoAteStr) {
          const criado = card.dt_criacao ? card.dt_criacao.slice(0, 10) : null;
          if (!criado) return false;
          if (criadoDeStr && criado < criadoDeStr) return false;
          if (criadoAteStr && criado > criadoAteStr) return false;
        }
        return true;
      }),
    }));
  }, [columns, search, estatutoByCardId, filtroCampos, filtroPrioridade, criadoDe, criadoAte]);

  // Contador de filtros ativos (p/ o badge do botão "Filtros").
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filtroCampos.responsavel) n += 1;
    if (filtroCampos.expiraDe) n += 1;
    if (filtroCampos.expiraAte) n += 1;
    if (filtroPrioridade) n += 1;
    if (criadoDe) n += 1;
    if (criadoAte) n += 1;
    return n;
  }, [filtroCampos, filtroPrioridade, criadoDe, criadoAte]);

  const total = estatutosData?.length ?? estatutoByCardId.size;

  const isLoading = loadingProjetos || (!!projetoId && loadingBoard) || loadingEstatutos;
  const error = errorProjetos || errorBoard || errorEstatutos;
  const projetoNaoEncontrado = !loadingProjetos && !errorProjetos && !projetoId;

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index)
      return;

    const cardId = parseInt(draggableId.replace('card-', ''), 10);
    const sourceColumnId = parseInt(source.droppableId.replace('column-', ''), 10);
    const destColumnId = parseInt(destination.droppableId.replace('column-', ''), 10);

    // Neighbors vêm das colunas renderizadas (filtradas) — os índices do dnd
    // são relativos ao que está na tela.
    const sourceColFiltered = filteredColumns.find(c => c.id === sourceColumnId);
    const destColFiltered = filteredColumns.find(c => c.id === destColumnId);
    if (!sourceColFiltered || !destColFiltered) return;

    let prevRank: string | null = null;
    let nextRank: string | null = null;

    if (sourceColumnId === destColumnId) {
      const cardsWithoutMoved = (sourceColFiltered.cards || []).filter(c => c.id !== cardId);
      const adjustedIndex =
        source.index < destination.index ? destination.index - 1 : destination.index;
      if (adjustedIndex === 0) {
        nextRank = cardsWithoutMoved[0]?.rank || null;
      } else if (adjustedIndex >= cardsWithoutMoved.length) {
        prevRank = cardsWithoutMoved[cardsWithoutMoved.length - 1]?.rank || null;
      } else {
        prevRank = cardsWithoutMoved[adjustedIndex - 1]?.rank || null;
        nextRank = cardsWithoutMoved[adjustedIndex]?.rank || null;
      }
    } else {
      const destCards = destColFiltered.cards || [];
      if (destination.index === 0) {
        nextRank = destCards[0]?.rank || null;
      } else if (destination.index >= destCards.length) {
        prevRank = destCards[destCards.length - 1]?.rank || null;
      } else {
        prevRank = destCards[destination.index - 1]?.rank || null;
        nextRank = destCards[destination.index]?.rank || null;
      }
    }

    const newRank = calculateRankBetween(prevRank, nextRank);

    // Update otimista sobre as colunas completas (não filtradas): move o card e
    // reordena o destino por rank.
    const sourceColumn = columns.find(c => c.id === sourceColumnId);
    const card = sourceColumn?.cards?.find(c => c.id === cardId);
    if (card) {
      const updatedCard = { ...card, rank: newRank };
      const newColumns = columns.map(col => {
        if (col.id === sourceColumnId && sourceColumnId !== destColumnId) {
          return { ...col, cards: (col.cards || []).filter(c => c.id !== cardId) };
        }
        if (col.id === destColumnId) {
          const newCards = (col.cards || []).filter(c => c.id !== cardId);
          newCards.push(updatedCard);
          newCards.sort((a, b) => a.rank.localeCompare(b.rank));
          return { ...col, cards: newCards };
        }
        return col;
      });
      setOptimisticColumns(newColumns);
    }

    moveCardMutation.mutate(
      { id: cardId, column_id: destColumnId, prev_rank: prevRank, next_rank: nextRank },
      {
        onError: () => {
          toast.error('Não foi possível mover o estatuto. Tente novamente.');
        },
        onSettled: () => {
          setOptimisticColumns(null);
          queryClient.invalidateQueries({ queryKey: ['kanban-board', projetoId] });
          queryClient.invalidateQueries({ queryKey: ['estatutos', 'all'] });
          queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
        },
      },
    );
  };

  // `estatuto` opcional: cards avulsos (task rápida sem estatuto) também abrem — nesse caso
  // o modal genérico abre sem a aba "Estatuto"/ações (que só fazem sentido com estatuto).
  const handleCardClick = (estatuto: IEstatuto | undefined, card: IKanbanCard) => {
    setSelectedId(estatuto?.id ?? null);
    setSelectedCard(card); // o card vem direto do board — garante que o modal abre
    openDrawer();
  };

  const handleCloseDrawer = () => {
    closeDrawer();
    setSelectedId(null);
    setSelectedCard(null);
  };

  // Editar o card no modal (título/descrição/prioridade/expiração/responsáveis) — MÓDULO
  // Estatutos permite editar (é o dono do dado); só o ESPELHO no /kanban geral é read-only.
  // Invalida TODAS as queries afetadas por editar/arquivar — as mesmas que o slot de ações
  // do estatuto invalida (incl. dashboard/arquivados), p/ os KPIs e a aba Arquivados não
  // ficarem defasados ao arquivar pelo botão do card.
  const invalidarBoard = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban-board', projetoId] });
    queryClient.invalidateQueries({ queryKey: ['estatutos', 'all'] });
    queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['estatuto-arquivados'] });
  };
  const salvarCardMutation = useMutation({
    mutationFn: updateCard,
    onSuccess: () => {
      toast.success('Card atualizado');
      invalidarBoard();
    },
    onError: () => toast.error('Erro ao atualizar o card'),
  });
  const arquivarCardMutation = useMutation({
    // Envolvido porque `arquivarCard` agora aceita um 2o argumento opcional
    // (motivo) e o MutationFunction do TanStack espera aridade 1.
    mutationFn: (cardId: number) => arquivarCard(cardId),
    onSuccess: () => {
      toast.success('Card arquivado');
      invalidarBoard();
      handleCloseDrawer();
    },
    onError: () => toast.error('Erro ao arquivar o card'),
  });

  const handleSaveCard = (
    id: number,
    titulo: string,
    descricao: string,
    prioridade: KanbanPrioridade,
    expiraEm?: string | null,
    campoValores?: IKanbanCampoValorInput[],
    userIds?: number[],
    vinculos?: IKanbanVinculo[],
  ) => {
    salvarCardMutation.mutate({
      id,
      titulo,
      descricao,
      prioridade,
      expira_em: expiraEm,
      campo_valores: campoValores,
      user_ids: userIds,
      vinculos,
    });
  };

  const handleArchiveCard = (id: number) => arquivarCardMutation.mutate(id);

  // Ações/estado do estatuto (o que era o EstatutoDrawer) — agora injetados no modal
  // GENÉRICO: as ações vão na sidebar (slot) e os dados numa aba "Estatuto". Um só modal
  // no sistema (o mesmo do Kanban geral), sem drawer próprio.
  const estatutoActions = useEstatutoCardActions({
    isOpen: isDrawerOpen,
    estatutoId: selectedId,
    onClose: handleCloseDrawer,
    canFinalizar,
    onFinalizar: handleFinalizar,
    finalizando: finalizarMutation.isPending,
  });

  return (
    <PageBox>
      <Box minH="100vh" bg="gray.50">
        {/* Header */}
        <Box
          bg="white"
          borderBottomWidth="1px"
          borderColor="gray.100"
          px={{ base: 4, md: 8 }}
          py={5}
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            gap={4}
            flexDir={{ base: 'column', md: 'row' }}
            mb={4}
          >
            <Flex align="center" gap={3}>
              <Box
                w="48px"
                h="48px"
                borderRadius="xl"
                bg="#EDE9FE"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <GraduationCap size={26} color="#7C3AED" weight="duotone" />
              </Box>
              <Box>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                >
                  Módulo
                </Text>
                <Heading
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="700"
                  color="gray.900"
                  letterSpacing="-0.02em"
                >
                  Estatutos — Comissão de Formatura
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {total} {total === 1 ? 'estatuto' : 'estatutos'} cadastrados
                </Text>
              </Box>
            </Flex>

            <Flex gap={2} align="center">
              {/* Atalho: criação RÁPIDA de um estatuto/baixa (nome + etapa) — cria o
                  processo real, sem cartório/advogado (edita depois). Só nos quadros
                  (Constituição/Baixa), onde há colunas p/ escolher a etapa. */}
              {/* Alteração: sem cartório o rápido só criaria em "Enviar para Assinatura", sem anexo. */}
              {VIEWS_QUADRO.includes(view) && view !== 'alteracao' && (
                <Button
                  {...BOTAO_SECUNDARIO}
                  leftIcon={<Plus size={14} weight="bold" />}
                  onClick={openQuickTask}
                >
                  Criar rápido
                </Button>
              )}
              {/* O que o presidente vai preencher: lista para mandar antes do link (26/10).
                  A Alteração ainda não tem formulário. */}
              {view !== 'alteracao' && (
                <Button
                  {...BOTAO_SECUNDARIO}
                  leftIcon={<ListChecks size={14} weight="bold" />}
                  onClick={perguntas.onOpen}
                >
                  Perguntas do formulário
                </Button>
              )}
              <Button
                {...BOTAO_CABECALHO}
                colorScheme="purple"
                leftIcon={<Plus size={14} weight="bold" />}
                onClick={openCreate}
              >
                Novo estatuto
              </Button>
            </Flex>
          </Flex>

          {/* Abas + filtros */}
          <Flex gap={3} flexWrap="wrap" align="center">
            <Flex bg="gray.100" borderRadius="lg" p={1} gap={1}>
              {(
                [
                  { key: 'dashboard', label: 'Dashboard', icon: ChartBar },
                  { key: 'constituicao', label: 'Constituição', icon: SquaresFour },
                  { key: 'baixa', label: 'Baixa', icon: SquaresFour },
                  { key: 'alteracao', label: 'Alteração', icon: SquaresFour },
                  { key: 'arquivados', label: 'Arquivados', icon: Archive },
                ] as { key: EstatutoView; label: string; icon: typeof SquaresFour }[]
              ).map(tab => {
                const active = view === tab.key;
                return (
                  <Button
                    key={tab.key}
                    size="sm"
                    variant="ghost"
                    bg={active ? 'white' : 'transparent'}
                    color={active ? 'purple.600' : 'gray.500'}
                    boxShadow={active ? 'sm' : 'none'}
                    fontWeight={active ? '700' : '600'}
                    leftIcon={<tab.icon size={14} weight={active ? 'fill' : 'regular'} />}
                    _hover={{ bg: active ? 'white' : 'gray.50' }}
                    onClick={() => setView(tab.key)}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </Flex>

            {VIEWS_QUADRO.includes(view) && (
              <Flex gap={2} align="center">
                <InputGroup maxW="320px" size="sm">
                  <InputLeftElement pointerEvents="none">
                    <MagnifyingGlass size={14} color="#94A3B8" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar por título..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    borderRadius="lg"
                  />
                </InputGroup>

                {/* Botão de filtros (abre a barra inline) + badge de filtros ativos */}
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Funnel size={15} weight={activeFilterCount > 0 ? 'fill' : 'bold'} />}
                  borderRadius="full"
                  color={activeFilterCount > 0 ? 'purple.600' : 'gray.500'}
                  bg={activeFilterCount > 0 ? 'purple.50' : 'transparent'}
                  _hover={{ bg: activeFilterCount > 0 ? 'purple.100' : 'gray.100' }}
                  onClick={() => setShowFilters(v => !v)}
                  fontSize="13px"
                  fontWeight="500"
                >
                  Filtros
                  {activeFilterCount > 0 && (
                    <Flex
                      ml={1.5}
                      bg="purple.500"
                      color="white"
                      w="18px"
                      h="18px"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="700"
                      align="center"
                      justify="center"
                    >
                      {activeFilterCount}
                    </Flex>
                  )}
                </Button>
              </Flex>
            )}
          </Flex>

          {/* Barra de filtros inline (só nos quadros; aplica no cache) */}
          {VIEWS_QUADRO.includes(view) && showFilters && (
            <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.100">
              <Flex gap={3} wrap="wrap" align="flex-end">
                {/* Prioridade */}
                <Box>
                  <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                    Prioridade
                  </Text>
                  <Select
                    size="sm"
                    w="150px"
                    borderRadius="md"
                    value={filtroPrioridade}
                    onChange={e => setFiltroPrioridade(e.target.value as '' | KanbanPrioridade)}
                  >
                    <option value="">Todas</option>
                    <option value="alta">Alta</option>
                    <option value="media">Média</option>
                    <option value="baixa">Baixa</option>
                  </Select>
                </Box>
                {/* Data de início (dt_criacao) */}
                <Box>
                  <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                    Início de
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    w="160px"
                    borderRadius="md"
                    value={criadoDe}
                    onChange={e => setCriadoDe(e.target.value)}
                  />
                </Box>
                <Box>
                  <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                    Início até
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    w="160px"
                    borderRadius="md"
                    value={criadoAte}
                    onChange={e => setCriadoAte(e.target.value)}
                  />
                </Box>
              </Flex>

              {/* Responsável + faixa de expiração (SLA/data-fim) — componente reusado */}
              <Box mt={3}>
                <KanbanFieldFilters
                  campos={[]}
                  filtros={filtroCampos}
                  usuarios={projectUsers}
                  onChange={setFiltroCampos}
                />
              </Box>

              {activeFilterCount > 0 && (
                <Button
                  mt={3}
                  size="xs"
                  variant="link"
                  colorScheme="purple"
                  onClick={() => {
                    setFiltroCampos({});
                    setFiltroPrioridade('');
                    setCriadoDe('');
                    setCriadoAte('');
                  }}
                >
                  Limpar filtros
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box px={{ base: 4, md: 6 }} py={6}>
          {view === 'dashboard' && (
            <EstatutoDashboard dashboard={dashboard} isLoading={loadingDashboard} />
          )}

          {view === 'arquivados' &&
            (projetoNaoEncontrado ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={20}
                gap={3}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="orange.100"
              >
                <Text fontSize="sm" color="orange.500" fontWeight="600">
                  Projeto &quot;{PROJETO_TITULO}&quot; não encontrado no kanban
                </Text>
              </Flex>
            ) : (
              <EstatutoArquivados projetoId={projetoId} />
            ))}

          {VIEWS_QUADRO.includes(view) && (
            <>
              {isLoading && (
                <Flex justify="center" py={20}>
                  <Spinner size="xl" color="purple.500" thickness="3px" />
                </Flex>
              )}

              {!isLoading && projetoNaoEncontrado && (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={20}
                  gap={3}
                  bg="white"
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="orange.100"
                >
                  <Text fontSize="sm" color="orange.500" fontWeight="600">
                    Projeto &quot;{PROJETO_TITULO}&quot; não encontrado no kanban
                  </Text>
                  <Text fontSize="xs" color="gray.500" textAlign="center" maxW="420px">
                    O board de Estatutos depende de um projeto de kanban chamado &quot;
                    {PROJETO_TITULO}&quot;. Crie esse projeto no módulo de Kanban para que os cards
                    apareçam aqui.
                  </Text>
                </Flex>
              )}

              {!isLoading && !projetoNaoEncontrado && error && (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={20}
                  gap={3}
                  bg="white"
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="red.100"
                >
                  <Text fontSize="sm" color="red.500" fontWeight="600">
                    Erro ao carregar os estatutos
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Verifique sua conexão e tente novamente.
                  </Text>
                </Flex>
              )}

              {!isLoading && !error && !projetoNaoEncontrado && (
                <>
                  <EstatutoKpiRow dashboard={dashboard} isLoading={loadingDashboard} />
                  <EstatutoBoard
                    columns={filteredColumns}
                    estatutoByCardId={estatutoByCardId}
                    onCardClick={handleCardClick}
                    onDragEnd={handleDragEnd}
                    // Colunas neutras (sem cores) nos DOIS quadros — Constituição e Baixa.
                    hideColors
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      <EstatutoPerguntasModal
        isOpen={perguntas.isOpen}
        onClose={perguntas.onClose}
        abaInicial={destinoAtivo === 'baixa' ? 1 : 0}
      />
      <EstatutoCreateModal
        isOpen={isCreateOpen}
        onClose={closeCreate}
        destinoInicial={destinoAtivo}
      />
      {/* Facilitador de task rápida — cria um card na etapa escolhida do quadro ativo. */}
      <EstatutoQuickTaskModal
        isOpen={isQuickTaskOpen}
        onClose={closeQuickTask}
        columns={columns}
        destino={destinoAtivo}
        projetoId={projetoId}
      />
      {/* Modal GENÉRICO (o mesmo do Kanban geral) — as ações do estatuto vão na sidebar
          (slot) e os dados numa aba "Estatuto". Substitui o EstatutoDrawer próprio. */}
      <KanbanCardModal
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        card={selectedCard}
        columnTitle={selectedCardColuna}
        // No MÓDULO Estatutos o card é EDITÁVEL (botão de editar do modal genérico, gated por
        // isCreator) — diferente do espelho no /kanban geral, que fica read-only. As ações do
        // estatuto (validar/Autentique/etc.) continuam no slot da sidebar.
        onSave={handleSaveCard}
        onArchive={handleArchiveCard}
        // Slots do estatuto só quando o card TEM estatuto vinculado. Card avulso (task rápida
        // criada pelo facilitador) abre o modal genérico puro — sem a aba "Estatuto" nem as
        // ações de estatuto (que dependem de `estatutoActions.estatuto`).
        sidebarSlot={selectedEstatuto ? <EstatutoAcoesSlot actions={estatutoActions} /> : undefined}
        // O EstatutoAcoesSlot já expõe o próprio "Arquivar" na sidebar — esconde o "Arquivar
        // card" genérico só quando o slot está presente (card de estatuto).
        hideArchive={!!selectedEstatuto}
        extraTab={
          selectedEstatuto
            ? {
                key: 'estatuto',
                label: 'Estatuto',
                icon: <FileText size={13} weight="bold" />,
                content: (
                  <EstatutoDadosTab actions={estatutoActions} columnTitle={selectedCardColuna} />
                ),
              }
            : undefined
        }
      />
      {/* Modais de Assinatura/PDF fora da aba condicional (estado no hook) — não desmontam
          ao trocar de aba no modal genérico. */}
      {estatutoActions.estatuto && (
        <EstatutoAssinaturaModal
          isOpen={estatutoActions.isAssinaturaOpen}
          onClose={estatutoActions.closeAssinatura}
          estatuto={estatutoActions.estatuto}
        />
      )}
      {estatutoActions.estatuto && (
        <EstatutoPdfModal
          isOpen={estatutoActions.isPdfOpen}
          onClose={estatutoActions.closePdf}
          estatutoId={estatutoActions.estatuto.id}
          titulo={estatutoActions.estatuto.titulo || `Estatuto #${estatutoActions.estatuto.id}`}
        />
      )}
    </PageBox>
  );
};

export default EstatutoPage;
